import React, { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Button from "components/ui/Button";
import Icon from "components/AppIcon";
import { SETUP_MODULES, SETUP_MODULES_BY_SLUG, getSetupPath } from "config/setupModules";
import useAuth from "hooks/useAuth";
import { getAllowedSetupModules } from "utils/setupAccess";

export default function SetupHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const availableModules = useMemo(() => getAllowedSetupModules(user), [user]);

  const activeModule = useMemo(() => {
    const setupSlug = location.pathname.replace(/^\/setup\/?/, "").split("/")[0];
    const currentModule = SETUP_MODULES_BY_SLUG[setupSlug];

    if (currentModule && availableModules.some((moduleConfig) => moduleConfig.slug === currentModule.slug)) {
      return currentModule;
    }

    return availableModules[0] || SETUP_MODULES[0];
  }, [availableModules, location.pathname]);

  return (
    <header className="sticky top-0 z-30 border-b border-border bg-card/95 backdrop-blur">
      <div className="border-b border-border px-5 py-4 md:px-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-start gap-3 min-w-0">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-900 text-white shadow-sm shrink-0">
              <Icon name="Settings2" className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                Setup Mode
              </p>
              <h1 className="text-xl font-semibold text-foreground">Configuration Workspace</h1>
              <p className="text-sm text-muted-foreground">
                Switch between setup modules, review configuration sections, and return to the dashboard when finished.
              </p>
            </div>
          </div>

          <Button variant="outline" size="sm" onClick={() => navigate("/ghana/dashboard")}>
            <Icon name="ArrowLeft" className="h-4 w-4" />
            Return to Dashboard
          </Button>
        </div>
      </div>

      <div className="px-5 py-3 md:px-6">
        <nav className="overflow-x-auto">
          <div className="inline-flex min-w-full gap-2 sm:min-w-0">
            {availableModules.map((moduleConfig) => {
              const isActive = moduleConfig.slug === activeModule.slug;

              return (
                <button
                  key={moduleConfig.slug}
                  type="button"
                  onClick={() => navigate(getSetupPath(moduleConfig.slug))}
                  className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
                    isActive
                      ? "border-[#16325a] bg-[#16325a] text-white"
                      : "border-border bg-background text-foreground hover:bg-muted"
                  }`}
                >
                  <Icon name={moduleConfig.icon} className="h-4 w-4" />
                  {moduleConfig.label}
                </button>
              );
            })}
          </div>
        </nav>
      </div>
    </header>
  );
}
